import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@emotion/react';
import { Grid } from '@mui/material';

// Our Components
import ChangePassword from 'components/Settings/ChangePassword';
import Loader from 'components/Loader';
import SettingsPanel from 'components/Settings/SettingsForm';
import PlanBillingTab from 'components/Settings/PlanBillingTab';
import DynamicTabs from 'components/Tabs/DynamicTabs';

// Our Hooks
import useGetProfile from 'hooks/useGetProfile';

// Our Constants
import { ADVISOR } from 'shared/constants';

const TAB_NAME = [
	{
		type: 'My Profile'
	},
	{
		type: 'Change Password'
	}
];

function SettingsTabs({ tabPanelSelected, setTabPanelSelected }) {
	const SoraTheme = useTheme();
	const { disabledGrey } = SoraTheme.palette.primary;

	const handleTabSelection = useCallback((e, value) => {
		setTabPanelSelected(value);
	}, []);

	// Get user profile data
	const { isLoading, data: userProfileData } = useGetProfile();

	// These are different based on role
	const tabPanelItems = useMemo(() => {
		const isAdvisor = userProfileData?.role === ADVISOR;

		const items = [
			{
				children: <SettingsPanel />
			},
			{
				children: <ChangePassword />
			}
		];

		if (isAdvisor) return [...items, { children: <PlanBillingTab /> }];

		return items;
	}, [userProfileData]);

	// These are different based on role
	const tabItems = useMemo(() => {
		const isAdvisor = userProfileData?.role === ADVISOR;
		if (isAdvisor) return [...TAB_NAME, { type: 'Plan & Billing' }];

		return TAB_NAME;
	}, [userProfileData]);

	if (isLoading) return <Loader />;

	return (
		<Grid container item xs={12}>
			<DynamicTabs
				tabItems={tabItems}
				tabItemSX={{
					padding: 0,
					textTransform: 'none',
					marginRight: 5
				}}
				tabsSX={{
					marginBottom: 4,
					borderBottom: `1px solid ${disabledGrey}`,
					'& .MuiTabs-scrollButtons.Mui-disabled': {
						opacity: 0.3
					},
					maxWidth: 'fit-content'
				}}
				tabPanelItems={tabPanelItems}
				value={tabPanelSelected}
				handleChange={handleTabSelection}
				aria-label="Settings tabs"
			/>
		</Grid>
	);
}

SettingsTabs.propTypes = {
	tabPanelSelected: PropTypes.number.isRequired,
	setTabPanelSelected: PropTypes.func.isRequired
};

export default SettingsTabs;
